'use client';

import React from 'react';

import styled, { css, keyframes } from 'styled-components';

import { createGradientOrSolid, type Gradient } from '@/lib/gradient';

/**
 * Portal-owned copy of shared-ui's LoginButton (v0.5.383) — the login screen
 * must not pay for the library. Styled CSS is verbatim; the two MUI pieces are
 * replaced 1:1 — CircularProgress (size 18, white) by an equivalent CSS
 * spinner, Stack direction="row" by the flex row it renders as.
 */

interface LoginButtonProps {
  callback?: () => void;
  text?: string;
  iconClass?: string;
  iconSrc?: string;
  iconSize?: string;
  type?: string;
  variant?: string;
  children?: React.ReactNode;
  // Callers also pass plain color strings (getButtonStyle's '#FFF' / '').
  // shared-ui's Gradient-typed props erased to `any`, so those always went
  // through; createGradientOrSolid falls back safely for them.
  textColor?: Gradient | string;
  backgroundColor?: Gradient | string;
  loading?: boolean;
  isDisabled?: boolean;
  opacityOnDisabled?: boolean;
  dataTestId?: string;
  isModalMode?: boolean;
}

export const LoginButton: React.FC<LoginButtonProps> = ({
  callback,
  text = 'login',
  type = 'button',
  variant,
  iconClass,
  iconSrc,
  iconSize = '24px',
  children,
  backgroundColor,
  textColor,
  loading = false,
  isDisabled = false,
  opacityOnDisabled = true,
  dataTestId = '',
  isModalMode = false,
}: LoginButtonProps) => {
  return (
    <StyledLoginButton
      type={type}
      className={`loginButton ${variant || ''}`}
      onClick={() => !isDisabled && callback?.()}
      backgroundColor={backgroundColor}
      textColor={textColor}
      disabled={isDisabled}
      data-testid={dataTestId}
      iconSize={iconSize}
      opacityOnDisabled={opacityOnDisabled}
      isModalMode={isModalMode}
    >
      {loading ? (
        <span className="loading-spinner" role="progressbar" aria-hidden />
      ) : (
        <>
          {iconClass && <i className={`prefix-icon ${iconClass}`} />}
          {iconSrc ? <img src={iconSrc} alt="icon" /> : null}
          <span
            style={{
              color: textColor ? createGradientOrSolid(textColor as Gradient) : '#00000',
            }}
            className="text-wrapper"
          >
            {children}
            <div>{text}</div>
          </span>
        </>
      )}
    </StyledLoginButton>
  );
};

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const StyledLoginButton = styled.button`
  width: 100%;
  height: ${(props: any) => (props.isModalMode ? '40px' : '50px')};
  margin-bottom: 12px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${(props: any) => (props.isModalMode ? '14px' : '16px')};
  font-weight: ${(props: any) => (props.isModalMode ? '400' : '600')};
  border-radius: ${(props: any) => (props.isModalMode ? '12px' : '8px')};
  background: ${(props: any) =>
    props.backgroundColor ? createGradientOrSolid(props.backgroundColor) : 'transparent'};
  border: ${(props: any) => (props.backgroundColor ? '0' : '1px solid white')};

  .loading-spinner {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.25);
    border-top-color: white;
    animation: ${spin} 0.8s linear infinite;
  }

  .prefix-icon {
    color: ${(props: any) => (props.textColor ? props.textColor.colorOne : 'white')};
    font-size: ${(props: any) => props.iconSize};
    margin-right: 8px;
  }

  &:disabled {
    cursor: not-allowed;
    ${(props: any) =>
      props.opacityOnDisabled &&
      css`
        opacity: 0.8;
      `};
  }

  .text-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;

    div {
      color: ${(props: any) => (props.textColor ? props.textColor.colorOne : 'white')};
    }
  }

  &.apple {
    background-color: black;
    border: 0;
  }

  &.google {
    border: 0;
    background-color: white;

    .text-wrapper {
      div {
        color: ${(props: any) =>
          props.textColor ? props.textColor.colorOne : 'rgba(0, 0, 0, 0.54)'};
      }
    }
  }

  &.facebook {
    border: 0;
    background-color: #3975ea;
  }

  &.facebookOutlined {
    background-color: transparent;
    border: 1px solid white;
  }

  &.white {
    background-color: white;

    .text-wrapper {
      div {
        color: ${(props: any) => (props.textColor ? props.textColor.colorOne : 'black')};
      }
    }
  }

  img {
    height: ${(props: any) => props.iconSize};
    width: ${(props: any) => props.iconSize};
    margin-right: 16px;
  }

  @media (min-width: 500px) {
    height: ${(props: any) => (props.isModalMode ? '40px' : '60px')};
  }
` as any;
